import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Input } from "@/components/ui/input";
import { useErrorToast } from "@/hooks/use-error-toast";
import {
  formatRuleCondition,
  getFieldById,
  getRuleValueForEditing,
  isSelectField,
  parseSelectFieldType,
  toApprovalRulePayload,
} from "@/lib/approvalRules";
import {
  useCreateApprovalRuleMutation,
  useDeleteApprovalRuleMutation,
  useUpdateApprovalRuleMutation,
} from "@/services/approvals/approvalsApi";
import { toast } from "sonner";

const fieldClass =
  "h-9 w-full min-w-0 rounded-[0.8rem] border border-line bg-background px-3 text-sm shadow-none";

function RuleValueField({ id, field, value, editable, onChange }) {
  if (!field) {
    return (
      <input
        type="text"
        disabled
        className={fieldClass}
        placeholder="Select a column first"
      />
    );
  }

  if (field.type === "datetime") {
    return (
      <input
        id={id}
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={!editable}
        required
        className={fieldClass}
      />
    );
  }

  if (isSelectField(field.type)) {
    const options = parseSelectFieldType(field.type) ?? [];
    return (
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={!editable}
        required
        className={fieldClass}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      id={id}
      type={field.type === "number" ? "number" : "text"}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={!editable}
      required
      className={fieldClass}
    />
  );
}

/**
 * Editing and deleting a rule are separate grants in the auth service, so they
 * are separate props. The single flag they replace defaulted to `true` and was
 * never passed by the page, which left both actions on for anyone who could
 * reach the screen — and reaching it needs only "Get approval rules List", a
 * read permission.
 */
export function ApprovalRuleCard({
  rule,
  columns,
  ruleNumber,
  onCancelDraft,
  canEdit = false,
  canDelete = false,
}) {
  const showErrorToast = useErrorToast();
  const [editable, setEditable] = useState(Boolean(rule.isNew));
  const [description, setDescription] = useState(rule.description ?? "");
  const [columnValue, setColumnValue] = useState(rule.field?.id ?? "");
  const [conditionValue, setConditionValue] = useState(rule.condition ?? "");
  const [value, setValue] = useState(() => getRuleValueForEditing(rule));
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [createRule, { isLoading: isCreating }] = useCreateApprovalRuleMutation();
  const [updateRule, { isLoading: isUpdating }] = useUpdateApprovalRuleMutation();
  const [deleteRule, { isLoading: isDeleting }] = useDeleteApprovalRuleMutation();

  const isSaving = isCreating || isUpdating;
  const selectedColumn = getFieldById(columnValue, columns);

  // Each column allows a different set of operators. Rather than storing a
  // condition that may not be valid and correcting it in an effect, the stored
  // value is only used when the chosen column actually allows it.
  const allowedConditions = selectedColumn?.allowedConditions ?? [];
  const condition =
    allowedConditions.some((item) => item.id === conditionValue)
      ? conditionValue
      : (allowedConditions[0]?.id ?? "");

  const handleCancel = () => {
    if (rule.isNew) {
      onCancelDraft?.();
      return;
    }
    setEditable(false);
    setDescription(rule.description ?? "");
    setColumnValue(rule.field?.id ?? "");
    setConditionValue(rule.condition ?? "");
    setValue(getRuleValueForEditing(rule));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!selectedColumn) return;

    const payload = toApprovalRulePayload({
      guid: rule.guid,
      description,
      fieldId: selectedColumn.id,
      condition,
      value,
    });

    try {
      if (rule.isNew) {
        await createRule(payload).unwrap();
        toast.success("Rule created");
        // The list refetches from the server, so the draft is simply dropped.
        onCancelDraft?.();
      } else {
        await updateRule({ ...payload, guid: rule.guid }).unwrap();
        toast.success("Rule updated");
        setEditable(false);
      }
    } catch (error) {
      showErrorToast(error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteRule(rule.guid).unwrap();
      toast.success("Rule deleted");
    } catch (error) {
      showErrorToast(error);
    } finally {
      setDeleteOpen(false);
    }
  };

  return (
    <>
      <article className="approval-rule-card">
        <form onSubmit={handleSave}>
          <div className="approval-rule-card-head">
            {editable ? (
              <div className="approval-rule-field approval-rule-field-grow">
                <label htmlFor={`rule-desc-${rule.guid}`} className="approval-rule-label">
                  Description
                </label>
                <Input
                  id={`rule-desc-${rule.guid}`}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  required
                  placeholder="Rule description"
                  className="h-10 rounded-[0.8rem] border-line bg-background shadow-none"
                />
              </div>
            ) : (
              <h3 className="approval-rule-title">
                <span className="num">{ruleNumber}.</span> {rule.description}
              </h3>
            )}

            <div className="approval-rule-actions">
              {!editable && canEdit && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  onClick={() => setEditable(true)}
                  aria-label="Edit rule"
                  className="rounded-[0.8rem] border-line shadow-none"
                >
                  <Pencil className="size-3.5" />
                </Button>
              )}
              {!rule.isNew && canDelete && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon-sm"
                  disabled={isDeleting}
                  onClick={() => setDeleteOpen(true)}
                  aria-label="Delete rule"
                  className="rounded-[0.8rem] border-line shadow-none"
                >
                  <Trash2 className="size-3.5" />
                </Button>
              )}
            </div>
          </div>

          <div className="approval-rule-condition">
            <span className="approval-rule-if">If</span>

            <div className="approval-rule-field">
              <label htmlFor={`rule-column-${rule.guid}`} className="approval-rule-label">
                Table column
              </label>
              <select
                id={`rule-column-${rule.guid}`}
                value={columnValue}
                onChange={(event) =>
                  setColumnValue(event.target.value === "" ? "" : Number(event.target.value))
                }
                disabled={!editable}
                required
                className={fieldClass}
              >
                <option value="">Select column</option>
                {columns.map((column) => (
                  <option key={column.id} value={column.id}>
                    {column.name}
                  </option>
                ))}
              </select>
            </div>

            {columnValue !== "" && selectedColumn && (
              <>
                <div className="approval-rule-field">
                  <label htmlFor={`rule-condition-${rule.guid}`} className="approval-rule-label">
                    Condition
                  </label>
                  <select
                    id={`rule-condition-${rule.guid}`}
                    value={condition}
                    onChange={(event) => setConditionValue(event.target.value)}
                    disabled={!editable}
                    required
                    className={fieldClass}
                  >
                    {allowedConditions.map((operator) => (
                      <option key={operator.id} value={operator.id}>
                        {operator.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="approval-rule-field">
                  <label htmlFor={`rule-value-${rule.guid}`} className="approval-rule-label">
                    Value
                  </label>
                  <RuleValueField
                    id={`rule-value-${rule.guid}`}
                    field={selectedColumn}
                    value={value}
                    editable={editable}
                    onChange={setValue}
                  />
                </div>
              </>
            )}
          </div>

          {!editable && selectedColumn && (
            <p className="approval-rule-summary">
              When <strong>{selectedColumn.name}</strong>{" "}
              {formatRuleCondition(condition, allowedConditions)}{ " "}
              <strong className="num">{value || "—"}</strong>
            </p>
          )}

          {editable && (
            <div className="approval-rule-footer">
              <Button
                type="button"
                variant="outline"
                className="h-9 rounded-[0.8rem] border-line shadow-none"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button type="submit" className="h-9 rounded-[0.8rem]" disabled={isSaving}>
                Save rule
              </Button>
            </div>
          )}
        </form>
      </article>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete rule"
        description={`Remove “${rule.description}”? This cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="destructive"
        onConfirm={handleDelete}
      />
    </>
  );
}
